// WebFooter.jsx

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ContactFormModal from '../../components/modals/web-modals/ContactFormModal';
import NewsletterModal from '../../components/modals/web-modals/NewsletterModal';
import AVIcon from '../../components/icons/AllVoicedIcons';

import { aboutFooterRoutes } from '../../navigation/publicAboutRoutes';
import { serviceRoutes } from '../../navigation/publicServiceRoutes';
import {
  publicHeaderActions,
  publicPrimaryNavigationItems,
} from '../../navigation/publicNavigationRoutes';

const catalogNavigationItem = publicPrimaryNavigationItems.find(
  (item) => item.id === 'catalog'
);

const openAppAction = publicHeaderActions.find(
  (item) => item.id === 'open-app'
);

function WebFooter() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const [isContactModalOpen, setContactModalOpen] = useState(false);
  const [isNewsletterModalOpen, setNewsletterModalOpen] = useState(false);

  const openContactModal = () => setContactModalOpen(true);
  const closeContactModal = () => setContactModalOpen(false);

  const openNewsletterModal = () => setNewsletterModalOpen(true);
  const closeNewsletterModal = () => setNewsletterModalOpen(false);

  return (
    <footer>
      <div className="footer-columns">
        {/* Brand area and operational app entry */}
        <div className="footer-column footer-brand-column">
          <Link
            to="/"
            className="brand footer-brand"
            aria-label={t('footer_home_label', 'All-Voiced home')}
          >
            <img
              src="/images/logo/logo-negative.svg"
              alt=""
              className="footer-logo"
            />
          </Link>

          {openAppAction && (
            <Link
              to={openAppAction.path}
              className="web-button footer-app-action"
            >
              {t(
                openAppAction.translationKey,
                openAppAction.label
              )}
            </Link>
          )}
        </div>

        {/* Explore */}
        <nav
          className="footer-column"
          aria-label={t('explore', 'Explore')}
        >
          <p className="footer-title">
            {t('explore', 'Explore')}
          </p>

          <ul className="footer-link-list">
            <li>
              <Link to="/">
                {t('home', 'Home')}
              </Link>
            </li>

            {catalogNavigationItem && (
              <li>
                <Link to={catalogNavigationItem.path}>
                  {t(
                    catalogNavigationItem.translationKey,
                    catalogNavigationItem.label
                  )}
                </Link>
              </li>
            )}

            <li>
              <Link to="/insights">
                {t('insights', 'Insights')}
              </Link>
            </li>
          </ul>
        </nav>

        {/* Services */}
        <nav
          className="footer-column"
          aria-label={t('services', 'Services')}
        >
          <p className="footer-title">
            {t('services', 'Services')}
          </p>

          <ul className="footer-link-list">
            {serviceRoutes.map((route) => (
              <li key={route.path}>
                <Link to={route.path}>
                  {route.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* About */}
        <nav
          className="footer-column"
          aria-label={t('about', 'About')}
        >
          <p className="footer-title">
            {t('about', 'About')}
          </p>

          <ul className="footer-link-list">
            {aboutFooterRoutes.map((route) => (
              <li key={route.path}>
                <Link to={route.path}>
                  {t(route.translationKey, route.label)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Legal */}
        <nav
          className="footer-column"
          aria-label={t('legal', 'Legal')}
        >
          <p className="footer-title">
            {t('legal', 'Legal')}
          </p>

          <ul className="footer-link-list">
            <li>
              <Link to="/legal-notice">
                {t('legal_notice', 'Legal Notice')}
              </Link>
            </li>

            <li>
              <Link to="/privacy-policy">
                {t('privacy_policy', 'Privacy Policy')}
              </Link>
            </li>

            <li>
              <Link to="/terms-and-conditions">
                {t(
                  'terms_and_conditions',
                  'Terms and Conditions'
                )}
              </Link>
            </li>
          </ul>
        </nav>

        {/* Connect */}
        <nav
          className="footer-column"
          aria-label={t('connect', 'Connect')}
        >
          <p className="footer-title">
            {t('connect', 'Connect')}
          </p>

          <ul className="footer-link-list">
            <li>
              <Link to="/connect">
                {t(
                  'explore_connect',
                  'Explore ways to connect'
                )}
              </Link>
            </li>

            <li>
              <button
                type="button"
                className="footer-link-button"
                onClick={openContactModal}
              >
                {t(
                  'send_us_a_message',
                  'Send us a message'
                )}
              </button>
            </li>

            <li>
              <button
                type="button"
                className="footer-link-button"
                onClick={openNewsletterModal}
              >
                {t(
                  'subscribe_to_newsletter',
                  'Subscribe to the newsletter'
                )}
              </button>
            </li>

            <li>
              <Link to="/meet">
                {t(
                  'schedule_meeting',
                  'Schedule a meeting'
                )}
              </Link>
            </li>
          </ul>

          <div
            className="social-media-links"
            aria-label={t(
              'social_media',
              'All-Voiced social media'
            )}
          >
            <a
              href="https://linkedin.com/company/allvoiced"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <AVIcon icon="linkedin" size={20} />
            </a>

            <a
              href="https://facebook.com/allvoiced"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <AVIcon icon="facebook" size={20} />
            </a>

            <a
              href="https://instagram.com/allvoiced"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <AVIcon icon="instagram" size={20} />
            </a>

            <a
              href="https://whatsapp.com/channel/0029VbBqyq0ADTOEAeMSI53N"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
            >
              <AVIcon icon="whatsapp" size={20} />
            </a>
          </div>
        </nav>
      </div>

      {/* Bottom line */}
      <div className="footer-bottom">
        <p>
          © {currentYear}{' '}
          <Link to="/">
            All-Voiced
          </Link>
          {' '}— Created by{' '}
          <a
            href="https://neuscalaf.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Neus Calaf, PhD, SLP
          </a>
          . All rights reserved.
        </p>

        <p className="footer-privacy-note">
          {t(
            'footer_privacy_note',
            'Built with privacy-first analytics · No tracking cookies'
          )}
        </p>
      </div>

      <ContactFormModal
        isOpen={isContactModalOpen}
        onRequestClose={closeContactModal}
      />

      <NewsletterModal
        isOpen={isNewsletterModalOpen}
        onRequestClose={closeNewsletterModal}
      />
    </footer>
  );
}

export default WebFooter;
